/**
 * `+ Add view` (`specs/M4-V1-views.md` §2.5), **280 wide**, in monday's exact order
 * `[cap:sandbox-add-view-menu.png]`.
 *
 * It reads the **view registry**, so a kind that lands stops being disabled without this file
 * changing: `implemented: false` renders the entry greyed with its module in the tooltip.
 * `Canvas`, the *Build with Vibe* block and `Apps ▸` are absent — monday-specific or AI
 * (`specs/M1-B1` §12 B1-12).
 */
import { galleryViewTypes } from '@elizmonday/shared';
import { Menu, type MenuEntry } from '../board-view/table/menus/Menu';

export interface GalleryEntry {
  registryId: string;
  label: string;
  /** `null` once the kind is built; otherwise the module that will build it. */
  module: string | null;
  badge?: string;
}

function entriesFor(section: 'board-views' | 'explore'): GalleryEntry[] {
  return galleryViewTypes(section).map((definition) => ({
    registryId: definition.id,
    label: definition.title,
    module: definition.implemented ? null : definition.plannedIn,
    ...(definition.id === 'kanban' ? { badge: 'New' } : {}),
  }));
}

/** Exported for the parity test: monday's own order, read from the registry. */
export const ADD_VIEW_ENTRIES: GalleryEntry[] = entriesFor('board-views');
export const MORE_VIEW_ENTRIES: GalleryEntry[] = entriesFor('explore');

export function AddViewGallery({
  x,
  y,
  onClose,
  onCreate,
}: {
  x: number;
  y: number;
  onClose(): void;
  onCreate(registryId: string): void;
}) {
  const toEntry = (entry: GalleryEntry): MenuEntry => ({
    id: entry.registryId,
    label: entry.badge ? `${entry.label} · ${entry.badge}` : entry.label,
    disabled: entry.module !== null,
    ...(entry.module === null ? { onSelect: () => onCreate(entry.registryId) } : {}),
  });

  const entries: MenuEntry[] = [
    { id: 'title', label: 'Board views', disabled: true },
    ...ADD_VIEW_ENTRIES.map(toEntry),
    { id: 'd1', label: '', divider: true },
    { id: 'explore', label: 'Explore more views', submenu: MORE_VIEW_ENTRIES.map(toEntry) },
  ];

  return <Menu entries={entries} x={x} y={y} width={280} label="Add view" onClose={onClose} />;
}
